import { getValueAsPercentage } from "util/number-utils";
import { observer } from "mobx-react";

import ContextMenu from "pages/common/components/context-menu/contextmenu";
import {
    DND_TRANSITION_HANDLE_LEFT,
    DND_TRANSITION_HANDLE_RIGHT
} from "pages/editor/lib/timeline";

import * as React from "react";

import { TransitionWrapper, TransitionIcon } from "./transition.styles";
import TransitionHandle from "./transitionHandle";

/**
 * @typedef TransitionProps
 * @property {TransitionStore}
 * @property {SectionStore}
 * @property {EditorUIStore}
 */

/**
 * @summary - Transition wrapper, responsible for the visually representing a transition on the timeline.
 * @param {TransitionProps} props
 * @returns {React.Element}
 */
const Transition = ({ transition, ui, section }) => {
    const [showContextMenu, setShowContextMenu] = React.useState(false);
    const [contextMenuState, setContextMenuState] = React.useState({});

    const closeContextMenu = () => {
        setShowContextMenu(false);
    };

    const onContextMenu = (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        setContextMenuState({
            contextMenuX: evt.pageX,
            contextMenuY: evt.pageY
        });
        setShowContextMenu(true);
    };

    const borderRadius = () => {
        if (transition.sectionFrom && transition.sectionTo) {
            return `5px`;
        }
        if (transition.sectionFrom) {
            return `5px 0 0 5px`;
        }
        if (transition.sectionTo) {
            return `0 5px 5px 0`;
        }
    };

    const transitionIcon = () => {
        if (transition.sectionFrom && transition.sectionTo) {
            return `crossfade`;
        }
        if (transition.sectionFrom) {
            return `fade-left`;
        }
        if (transition.sectionTo) {
            return `fade-right`;
        }
    };

    const leftPercentage = getValueAsPercentage(
        transition.startTime - section.startTime,
        section.duration
    );

    const widthPercentage = getValueAsPercentage(
        transition.duration,
        section.duration
    );

    const style = {
        left: `${leftPercentage}%`,
        width: `${widthPercentage}%`,
        borderRadius: borderRadius()
    };

    const active = ui.activeTransitionId === transition.id;

    return (
        <TransitionWrapper
            data-testid="timeline-transition"
            active={active}
            onClick={(evt) => {
                evt.stopPropagation();
                closeContextMenu();
                ui.onTransitionClick(transition.id);
            }}
            style={style}
            onContextMenu={onContextMenu}
        >
            <TransitionIcon
                active={active}
                iconUrl={`/static/img/${transitionIcon()}.svg`}
            />
            {transition.sectionFrom && (
                <TransitionHandle
                    side={DND_TRANSITION_HANDLE_LEFT}
                    transition={transition}
                    active={active}
                />
            )}
            {transition.sectionTo && (
                <TransitionHandle
                    side={DND_TRANSITION_HANDLE_RIGHT}
                    transition={transition}
                    active={active}
                />
            )}
            {showContextMenu && (
                <ContextMenu
                    positionX={contextMenuState.contextMenuX}
                    positionY={contextMenuState.contextMenuY}
                    items={[
                        {
                            id: `delete`,
                            onClick: transition.remove,
                            text: `Delete`
                        }
                    ]}
                    onClose={closeContextMenu}
                />
            )}
        </TransitionWrapper>
    );
};

export const Component = Transition;

export default observer(Transition);
